import { MICROUNITS_PER_CURRENCY } from "@/lib/billing-format"
import { EN_SHORT_MONTHS, RU_SHORT_MONTHS } from "@/lib/people-format"
import type { SpendLimitDefaultMember, SpendLimitMemberState } from "@/types/billing"

export const SPEND_LIMIT_AMBER_FROM = 0.8
export const SPEND_LIMIT_DANGER_FROM = 0.95
/** Как BillingSpendLimit::MAX_AMOUNT_MICROCENTS. */
export const SPEND_LIMIT_MAX_RUB = 1_000_000_000

export type SpendLimitTone = "accent" | "amber" | "danger"

const RU_MONTHS_PREPOSITIONAL = [
  "январе",
  "феврале",
  "марте",
  "апреле",
  "мае",
  "июне",
  "июле",
  "августе",
  "сентябре",
  "октябре",
  "ноябре",
  "декабре",
]

const EN_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]

/** Доля потраченного: min(потрачено / лимит, 1); при лимите 0 — 1; без лимита — null. */
export function spendLimitPercent(spentMicrocents: number, limitMicrocents: number | null | undefined): number | null {
  if (limitMicrocents == null) return null
  if (limitMicrocents <= 0) return 1
  return Math.min(Math.max(spentMicrocents, 0) / limitMicrocents, 1)
}

export function spendLimitRemaining(spentMicrocents: number, limitMicrocents: number): number {
  return Math.max(limitMicrocents - spentMicrocents, 0)
}

export function spendLimitTone(percent: number): SpendLimitTone {
  if (percent >= SPEND_LIMIT_DANGER_FROM) return "danger"
  if (percent >= SPEND_LIMIT_AMBER_FROM) return "amber"
  return "accent"
}

/** Постоянный лимит сотрудника: «Без лимита» — null, иначе своя сумма или умолчание. */
export function memberLimitMicrocents(row: {
  state: SpendLimitMemberState
  amount_microcents: number | null
}): number | null {
  return row.state === "unlimited" ? null : row.amount_microcents
}

/** Потолок этого месяца: постоянная сумма плюс надбавка. Без числового лимита надбавка не действует. */
export function memberEffectiveLimitMicrocents(row: {
  state: SpendLimitMemberState
  amount_microcents: number | null
  topup_microcents?: number
}): number | null {
  const standing = memberLimitMicrocents(row)
  if (standing == null) return null
  return standing + (row.topup_microcents ?? 0)
}

/** Члены под умолчанием от 80 %, по убыванию доли. */
export function membersNearLimit(members: SpendLimitDefaultMember[]): SpendLimitDefaultMember[] {
  return members
    .filter((row) => row.state === "default" && (row.percent ?? 0) >= SPEND_LIMIT_AMBER_FROM)
    .sort((a, b) => (b.percent ?? 0) - (a.percent ?? 0))
}

const NBSP = String.fromCharCode(0xa0)

export function formatSpendLimitPercent(percent: number): string {
  return `${Math.round(Math.min(Math.max(percent, 0), 1) * 100)}${NBSP}%`
}

export function rubToMicrocents(rub: number): number {
  return rub * MICROUNITS_PER_CURRENCY
}

/** Месяц периода — тот, что кончается в resets_at (UTC), в предложном падеже для русского. */
export function spendLimitMonthName(resetsAtIso: string, locale: string): string {
  const date = new Date(resetsAtIso)
  if (Number.isNaN(date.getTime())) return "—"
  const index = (date.getUTCMonth() + 11) % 12
  return locale.toLowerCase().startsWith("ru") ? RU_MONTHS_PREPOSITIONAL[index] : EN_MONTHS[index]
}

/** Дата сброса по часам зрителя, без года, как в серверном отказе: «1 окт» / «Oct 1». */
export function formatSpendLimitResetDate(iso: string, locale: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return "—"
  const ru = locale.toLowerCase().startsWith("ru")
  const month = (ru ? RU_SHORT_MONTHS : EN_SHORT_MONTHS)[date.getMonth()]
  return ru ? `${date.getDate()} ${month}` : `${month} ${date.getDate()}`
}

/** Время сброса по часам зрителя: «03:00» / «3:00 AM». */
export function formatSpendLimitResetTime(iso: string, locale: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return "—"
  return locale.toLowerCase().startsWith("ru")
    ? date.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })
    : date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
}

export const AMOUNT_GROUP_SEPARATOR = " "

/** Только цифры, без ведущих нулей («0050» → «50», «0» → «0»). */
export function amountDigits(value: string): string {
  return value.replace(/\D+/g, "").replace(/^0+(?=\d)/, "")
}

export function formatAmountDigits(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, AMOUNT_GROUP_SEPARATOR)
}

/** Поле суммы с разрядами при вводе, курсор остаётся после той же цифры (docs/billing_limits.md §18 п.7). */
export function formatAmountInput(raw: string, caret: number): { value: string; caret: number } {
  const rawDigits = raw.replace(/\D+/g, "")
  const digits = amountDigits(raw)
  const strippedZeros = rawDigits.length - digits.length
  const digitsBeforeCaret = raw.slice(0, Math.max(caret, 0)).replace(/\D+/g, "").length
  const before = Math.max(digitsBeforeCaret - strippedZeros, 0)
  const value = formatAmountDigits(digits)

  let seen = 0
  let position = value.length
  for (let index = 0; index < value.length; index += 1) {
    if (seen === before) {
      position = index
      break
    }
    if (/\d/.test(value[index])) seen += 1
  }
  if (seen < before) position = value.length
  return { value, caret: position }
}

/** Рубли из поля суммы; пустое поле — null. */
export function parseAmountRub(value: string): number | null {
  const digits = amountDigits(value)
  return digits === "" ? null : Number(digits)
}
